<script>
  import IntersectionObserver from "svelte-intersection-observer";
  import DepartmentWithTooltip from "@helpers/DepartmentWithTooltip.svelte";

  import Treemap from "../Treemap/Treemap.svelte";
  import Scrolly from "@helpers/Scrolly.svelte";
  import Emoji from "@helpers/Emoji.svelte";

  import {
    unselectedDepartmentsSorted,
    selectedDepartmentsSorted,
  } from "@stores/treemap.js";

  import { budgetBlocks } from "@stores/userData.js";

  let element;
  export let intersecting = false;

  import data from "@data/departmentBudgets.json";

  import { format } from "d3-format";
  const suffixFormat = format(",.3s");

  import { sum } from "d3-array";
  $: sumOfSelected = sum($selectedDepartmentsSorted.map((dept) => dept.amount));

  const sumOfAll = sum(data.map((d) => d.budget));
  const percentFormat = format(".2%");
  $: percentOfTotal = sumOfSelected / sumOfAll;

  $: userBlockMaxes = $selectedDepartmentsSorted.filter(
    (dept) => dept.isUserMax
  );
  let stepIndex;
</script>

{#if $budgetBlocks.length > 0}
  <IntersectionObserver {element} bind:intersecting>
    <section bind:this={element} id="context">
      {#key $selectedDepartmentsSorted.map((d) => d.name)}
        <div class="prose">
          <h1 class="section-title">
            How do your priorities <span
              class="accent-text emoji-follows"
              data-emoji="🧐"
              >compare?<Emoji emoji="monocle" />
            </span>
          </h1>
          <p class="body-text">
            The sum of your selected departments represents <strong
              >{percentFormat(percentOfTotal)} of the total budget for Fiscal Year
              2022.</strong
            >
          </p>
          <p class="direction-text">
            Scroll to see what the Fiscal Year 2022 budget looks like for your
            top departments.
          </p>
        </div>
        <div class="sticky-container">
          <!-- STICKY CHART -->
          <Treemap step={stepIndex} />

          <!-- SCROLLY TEXT STEPS -->
          <Scrolly bind:value={stepIndex}>
            <div class="step" class:active={stepIndex === 0}>
              <div class="step-content">
                <p>
                  Here's what you said you wanted your budget to look like.
                  {#if userBlockMaxes.length}
                    You gave the majority of your budget blocks to
                    <!-- {#key userBlockMaxes} -->
                    {#if userBlockMaxes.length > 1}
                      {#each userBlockMaxes as department, index}
                        <DepartmentWithTooltip
                          departmentName={department.name}
                        />{#if index <= userBlockMaxes.length - 2 && userBlockMaxes.length != 2},{:else if index == userBlockMaxes.length - 1}.{/if}
                        {#if index == userBlockMaxes.length - 2}
                          and&nbsp;{/if}
                      {/each}
                    {:else}
                      <DepartmentWithTooltip
                        departmentName={userBlockMaxes[0].name}
                      />.
                    {/if}
                    <!-- {/key}  -->
                    Nice!
                  {/if}
                </p>
              </div>
            </div>
            <div class="step" class:active={stepIndex === 1}>
              <div class="step-content">
                <p>
                  In Fiscal Year 2022, the distribution of those departments'
                  budgets looks like this.
                </p>
                <p>
                  The largest budget among your selected departments is
                  <DepartmentWithTooltip
                    departmentName={$selectedDepartmentsSorted[0].name}
                  />, with a budget of
                  <strong
                    >${suffixFormat(
                      $selectedDepartmentsSorted[0].amount
                    )}.</strong
                  >
                </p>
                {#if userBlockMaxes.length == 1 && userBlockMaxes[0].rank > 1}
                  <p>
                    <DepartmentWithTooltip
                      departmentName={userBlockMaxes[0].name}
                    />
                    ranked #{userBlockMaxes[0].rank}, with a budget of
                    <strong>${suffixFormat(userBlockMaxes[0].amount)}.</strong>
                  </p>{/if}
              </div>
            </div>
            <div class="step" class:active={stepIndex === 2}>
              <div class="step-content">
                <p>
                  The budget includes many more departments than the four you
                  selected.
                </p>
                <p>
                  For example, the best-funded department outside of your
                  priorities is
                  <DepartmentWithTooltip
                    departmentName={$unselectedDepartmentsSorted[0].name}
                  />. It had a budget of
                  <!-- FIXME: 'from the General Fund'? -->
                  <strong
                    >${suffixFormat(
                      $unselectedDepartmentsSorted[0].amount
                    )}</strong
                  > from the General Fund.
                </p>
                <p>
                  If we include the <DepartmentWithTooltip
                    departmentName={$unselectedDepartmentsSorted[0].name}
                  />, the budget looks like this:
                </p>
              </div>
            </div>
            <div class="step" class:active={stepIndex === 3}>
              <div class="step-content">
                <p>
                  And if we include the <em>entire General Fund</em>, the
                  relative importance of your preferences becomes clear.
                </p>
                <p>
                  The sum of your selected departments represents <strong
                    >{percentFormat(percentOfTotal)} of the total budget for Fiscal
                    Year 2022.
                  </strong>
                </p>
              </div>
            </div>
            <div class="step" class:active={stepIndex === 4}>
              <div class="step-content">
                <p>
                  Want to dive deeper? Explore the Houston budget by clicking on
                  rectangles; each will go one level deeper to show where
                  Houston's money is spent.
                </p>
              </div>
            </div>
            <div class="step buffer" class:active={stepIndex === 5} />
          </Scrolly>
        </div>
      {/key}
    </section>
  </IntersectionObserver>
{/if}

<!-- {/if} -->
<style>
  section {
    text-align: left;
    padding: 5rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    place-items: flex-start;
    border: 1px solid rgba(var(--text-color-rgb), 0.25);
    width: 100%;
  }

  .emoji-follows:before {
    top: 1rem;
    left: 100%;
  }
  .sticky-container {
    position: relative;
    width: 100%;
    z-index: 5;
    /* height: 100%; */
  }

  .step {
    height: 80vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }

  .step-content {
    /* border-radius: 5px; */
    background: whitesmoke;
    color: #ccc;
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.2);
    z-index: 10;
    border-left: 5px solid transparent;
    max-width: 668px;
    margin: auto 1rem;
  }

  .step.active .step-content {
    background: white;
    color: black;
    border-left: 5px solid var(--accent-color);
  }

  .step-content p {
    font-size: 1.25rem;
    line-height: 1.25;
    color: rgba(var(--text-color-rgb), 0.95);
    font-weight: 300;
  }

  .step-content p:not(:last-child) {
    margin-bottom: 1rem;
  }

  .step.buffer {
    height: 70vh;
  }
</style>
